import SiteNotification from '@app/model/site_notification/SiteNotification';
import React, { PropsWithChildren } from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import SiteNotificationService from '@app/model/site_notification/service/SiteNotificationService';
import { SiteNotificationQueryKeys } from '@app/model/site_notification/service/SiteNotificationQueryKeys';
import SiteNotificationType from '@app/model/site_notification/SiteNotificationType';
import _isEmpty from 'lodash/isEmpty';

export type SiteNotificationsTools = {
	notificationsPerType: Map<SiteNotificationType, SiteNotification[]>
	acknowledge: (id: number) => void,
	acknowledgeAll: (ids: number[]) => void,
	refetch: () => void
}

export const SiteNotificationsContext = React.createContext<SiteNotificationsTools>({
	notificationsPerType: new Map(),
	acknowledge: () => {
	},
	acknowledgeAll: () => {},
	refetch: () => {}
});

export const SiteNotificationsProvider: React.FunctionComponent<PropsWithChildren<{
	companyHotel: CompanyHotel
}>> = ({ children, companyHotel }) => {

	const queryClient = useQueryClient();

	const acknowledge = useMutation({
		mutationFn: SiteNotificationService.acknowledge,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SiteNotificationQueryKeys.notifications(companyHotel.id) });
		},
	});

	const refetch = () => {
		queryClient.refetchQueries({ queryKey: SiteNotificationQueryKeys.notifications(companyHotel.id) })
	}

	const handleMessage = () => refetch();

	const { data: notifications, isSuccess } = useQuery({
		queryFn: () => SiteNotificationService.findUnacknowledgedNotifications(companyHotel.id),
		refetchOnWindowFocus: false,
		queryKey: SiteNotificationQueryKeys.notifications(companyHotel.id),
	});

	const result: Map<SiteNotificationType, SiteNotification[]> = new Map();
	if(isSuccess && !_isEmpty(notifications)) {
		notifications.forEach(notification=>{
			const typeKey: SiteNotificationType = notification.type;
			if (!result.has(typeKey)) {
				result.set(typeKey, []);
			}
			result.get(typeKey)!.push(notification);
		})
	}

	return <SiteNotificationsContext.Provider value={{
		notificationsPerType: result,
		acknowledge: (id: number) => acknowledge.mutate(id),
		acknowledgeAll: (ids: number[]) => ids.forEach(id=>acknowledge.mutate(id)),
		refetch: refetch
	}}>
		{children}

	</SiteNotificationsContext.Provider>;
};